import React, { useState, useEffect } from 'react'
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  AppBar,
  Toolbar,
  IconButton,
  Grid,
  CircularProgress,
  Alert
} from '@mui/material'
import {
  ArrowBack as ArrowBackIcon,
  Quiz as QuizIcon,
  Lock as LockIcon,
  CheckCircle as CheckCircleIcon
} from '@mui/icons-material'
import { useAuth } from '../hooks/useAuth'
import { db } from '../firebase/config'
import { collection, doc, getDoc, getDocs } from 'firebase/firestore'
import toast from 'react-hot-toast'
import Quiz from './Quiz'

const QuizLevelSelection = ({ userData, userLevel, onBack }) => {
  const { user } = useAuth()
  const [loading, setLoading] = useState(true)
  const [currentUserLevel, setCurrentUserLevel] = useState(userLevel || 'A1')
  const [levels, setLevels] = useState([])
  const [completedLevels, setCompletedLevels] = useState([])
  const [currentPage, setCurrentPage] = useState('quiz-level-selection')
  const [selectedLevel, setSelectedLevel] = useState(null)

  useEffect(() => {
    if (user && userData) {
      const level = userLevel || userData.level || 'A1'
      setCurrentUserLevel(level)
      loadCompletedLevels(level)
    }
  }, [user, userData, userLevel])

  useEffect(() => {
    if (user && userData && currentUserLevel) {
      loadLevels(currentUserLevel)
    }
  }, [user, userData, currentUserLevel, completedLevels])

  const loadCompletedLevels = async (level) => {
    try {
      console.log('Loading completed quiz levels for user:', user.uid, 'level:', level)
      const firestoreLevel = level.toUpperCase()
      
      // /gameProgress/A1/quiz/{userId}/completedLevels koleksiyonundan tüm level'ları çek
      const completedLevelsRef = collection(db, 'gameProgress', firestoreLevel, 'quiz', user.uid, 'completedLevels')
      const completedLevelsSnapshot = await getDocs(completedLevelsRef)
      
      const completed = []
      completedLevelsSnapshot.forEach(doc => {
        const data = doc.data()
        if (data.completed) {
          const levelNumber = parseInt(doc.id.replace('level_', ''))
          completed.push(levelNumber)
        }
      })
      
      setCompletedLevels(completed)
      console.log('Completed quiz levels from Firebase:', completed)
    } catch (error) {
      console.error('Error loading completed quiz levels:', error)
      setCompletedLevels([])
    }
  }

  const loadLevels = async (level) => {
    try {
      setLoading(true)
      console.log('Loading quiz levels for:', level)
      
      const firestoreLevel = level.toUpperCase()
      const fieldName = firestoreLevel === 'EXAM' ? 'questions' : 'questions'
      
      // quizzes/{userLevel}/levels koleksiyonundan tüm level'ları çek
      const levelsRef = collection(db, 'quizzes', firestoreLevel, 'levels')
      const levelsSnapshot = await getDocs(levelsRef)
      
      const levelsList = []
      levelsSnapshot.forEach(doc => {
        const levelNumber = parseInt(doc.id)
        const data = doc.data()
        const levelQuestions = data[fieldName] || []
        const isCompleted = completedLevels.includes(levelNumber)
        const isNextLevel = levelNumber === 1 || completedLevels.includes(levelNumber - 1)
        const isAvailable = isCompleted || isNextLevel

        console.log(`Quiz Level ${levelNumber}: completed=${isCompleted}, available=${isAvailable}, questionCount=${levelQuestions.length}`)

        if (levelQuestions.length > 0) {
          levelsList.push({
            level: levelNumber,
            questionCount: levelQuestions.length,
            available: isAvailable,
            completed: isCompleted
          })
        }
      })
      
      // Level'ları sırala
      levelsList.sort((a, b) => a.level - b.level)
      setLevels(levelsList)
    } catch (error) {
      console.error('Error loading quiz levels:', error)
      toast.error('Quiz level\'ları yüklenirken hata oluştu')
    } finally {
      setLoading(false)
    }
  }

  const handleLevelClick = (level) => {
    if (!level.available) {
      toast.error('Bu quiz level\'ı henüz kilitli!')
      return
    }
    
    console.log('Selected quiz level:', level.level)
    setCurrentPage('quiz')
    setSelectedLevel(level.level)
  }

  const handleBackToQuizLevelSelection = (data) => {
    if (data && data.page) {
      setCurrentPage(data.page)
    } else {
      setCurrentPage('quiz-level-selection')
    }
  }

  if (loading) {
    return (
      <Box sx={{ flexGrow: 1 }}>
        <AppBar position="static">
          <Toolbar>
            <IconButton
              edge="start"
              color="inherit"
              onClick={() => {
                console.log('QuizLevelSelection back button clicked, onBack:', onBack)
                if (onBack && typeof onBack === 'function') {
                  onBack()
                } else {
                  console.error('onBack is not a function:', onBack)
                }
              }}
              sx={{ mr: 2 }}
            >
              <ArrowBackIcon />
            </IconButton>
            <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
              Quiz - Level Seçimi
            </Typography>
          </Toolbar>
        </AppBar>
        <Container maxWidth="md" sx={{ py: 3 }}>
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <CircularProgress />
            <Typography sx={{ mt: 2 }}>Yükleniyor...</Typography>
          </Box>
        </Container>
      </Box>
    )
  }

  // Quiz oyununu göster
  if (currentPage === 'quiz' && selectedLevel) {
    return <Quiz 
      userData={userData} 
      onBack={handleBackToQuizLevelSelection}
      selectedLevel={selectedLevel}
    />
  }

  return (
    <Box sx={{ flexGrow: 1 }}>
      <AppBar position="static" sx={{ 
        background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
        boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
      }}>
        <Toolbar>
          <IconButton
            edge="start"
            color="inherit"
            onClick={() => {
              console.log('QuizLevelSelection main back button clicked, onBack:', onBack)
              if (onBack && typeof onBack === 'function') {
                onBack()
              } else {
                console.error('onBack is not a function:', onBack)
              }
            }}
            sx={{ mr: 2 }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            Quiz - Level Seçimi
          </Typography>
          <Typography variant="body2" sx={{ mr: 2 }}>
            {currentUserLevel}
          </Typography>
        </Toolbar>
      </AppBar>

      <Container maxWidth="md" sx={{ py: 3 }}>
        <Typography variant="h4" sx={{ mb: 3, textAlign: 'center' }}>
          Quiz Level Seçin
        </Typography>

        {levels.length === 0 ? (
          <Alert severity="info">
            Bu seviye için henüz quiz level'ı bulunmuyor.
          </Alert>
        ) : (
          <Grid container spacing={2}>
            {levels.map((level) => (
              <Grid item xs={6} sm={4} md={3} key={level.level}>
                <Card
                  sx={{
                    cursor: level.available ? 'pointer' : 'not-allowed',
                    opacity: level.available ? 1 : 0.6,
                    border: level.completed ? '3px solid #4caf50' : level.available ? '3px solid #1976d2' : '3px solid #ccc',
                    backgroundColor: level.completed ? '#e8f5e8' : level.available ? '#f3f8ff' : '#f5f5f5',
                    transition: 'all 0.3s ease',
                    '&:hover': level.available ? {
                      transform: 'translateY(-4px)',
                      boxShadow: 4
                    } : {}
                  }}
                  onClick={() => handleLevelClick(level)}
                >
                  <CardContent sx={{ textAlign: 'center', p: 2 }}>
                    <Box sx={{ mb: 1 }}>
                      {level.completed ? (
                        <CheckCircleIcon sx={{ fontSize: 32, color: '#4caf50' }} />
                      ) : level.available ? (
                        <QuizIcon sx={{ fontSize: 32, color: '#1976d2' }} />
                      ) : (
                        <LockIcon sx={{ fontSize: 32, color: '#ccc' }} />
                      )}
                    </Box>
                    <Typography variant="h6" sx={{ mb: 1 }}>
                      Level {level.level}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {level.questionCount} soru
                    </Typography>
                    {level.completed && (
                      <Typography variant="caption" sx={{ color: '#4caf50', fontWeight: 'bold' }}>
                        Tamamlandı
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </Container>
    </Box>
  )
}

export default QuizLevelSelection
